import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Heart, Lock, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLocale } from "../../context/LocaleContext";
import Seo from "../../components/Seo";
import { Button } from "../../components/ui/button";

export default function DashboardPage() {
  const { user, loading } = useAuth();
  const { t, lp } = useLocale();
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    if (user) api.get("/api/me/favorites").then(({ data }) => setFavorites(data.items)).catch(() => {});
  }, [user]);

  async function remove(id) {
    await api.delete(`/api/me/favorites/${id}`);
    setFavorites((f) => f.filter((x) => x.id !== id));
  }

  if (loading) return <div className="container-page py-24 text-center text-slate-400">{t("loading")}</div>;
  if (!user) return <Navigate to={lp("/login")} replace />;

  return (
    <div className="container-page py-12">
      <Seo title={`${t("dashboard")} | LIVYVIO`} />
      <div className="flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-600 text-xl font-bold text-white">
          {user.name?.[0]?.toUpperCase()}
        </div>
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">{user.name}</h1>
          <p className="text-sm text-slate-500">{user.email} · <span className="capitalize">{user.role}</span></p>
        </div>
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-slate-900"><Heart className="h-5 w-5 text-emerald-600" />{t("myFavorites")}</h2>
          {favorites.length === 0 ? (
            <p className="mt-4 rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-500">{t("noFavorites")}</p>
          ) : (
            <div className="mt-4 space-y-3">
              {favorites.map((f) => (
                <div key={f.id} data-testid={`fav-${f.id}`} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-3">
                  {f.image && <img src={f.image} alt="" className="h-14 w-20 rounded-lg object-cover" />}
                  <Link to={lp(`/${f.section}/${f.slug}`)} className="flex-1 font-medium text-slate-800 hover:text-emerald-700">{f.title}</Link>
                  <button onClick={() => remove(f.id)} data-testid={`fav-remove-${f.id}`} className="rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </section>

        <aside>
          <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-6">
            <div className="flex items-center gap-2 text-emerald-700"><Lock className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wider">LIVYVIO+</span></div>
            <h3 className="mt-3 font-heading text-lg font-bold text-slate-900">{t("premiumTitle")}</h3>
            <p className="mt-2 text-sm text-slate-600">{t("premiumBody")}</p>
            <span data-testid="premium-locked" className="mt-4 flex w-full items-center justify-center rounded-full border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-700">
              <Lock className="mr-1.5 h-3.5 w-3.5" />{t("comingSoon")}
            </span>
          </div>
        </aside>
      </div>
    </div>
  );
}
